import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import { ChartConfig, ChartPoint, ChartSeries, ChartType } from './chart.model';

Chart.register(...registerables);

const PALETTE = ['#27aae1', '#2bb673', '#8d6678', '#f59e0b', '#6196bb', '#ef4444'];
const DONUT_TYPES = new Set<ChartType>(['donut', 'pie']);
const MAX_TOOLTIP_NAMES = 6;
/** Half the widest tooltip we expect, so a centred one can't be pushed off either edge of the viewport. */
const TOOLTIP_EDGE_MARGIN_PX = 110;
/** Below this the tooltip would sit above the top of the viewport, so it flips under its anchor instead. */
const TOOLTIP_FLIP_MARGIN_PX = 90;

/** One line of the DOM tooltip: a swatch, the point's label, its value, and any contributing names. */
interface TooltipRow {
  label: string;
  value: string;
  color: string;
  note: string;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

// Chart.js draws text on canvas, not the DOM — it can't resolve CSS custom properties (var(--...)),
// so theme font/colors are hardcoded here to match _tokens.scss (--font-sans / --h4c-dark-blue / --fg-3).
const CHART_FONT_FAMILY =
  "'Poppins', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
const LEGEND_LABEL_COLOR = '#262262'; // --h4c-dark-blue
const AXIS_LABEL_COLOR = '#6b7280'; // --fg-3
const CHART_SURFACE = '#ffffff'; // --bg-card

/**
 * Alpha appended to a 6-digit hex for a `softFill` bar — 0xa6 is 65%. Nothing is drawn around the
 * band, so the fill is the whole mark and cannot go as light as an area's 0x33, which has a line on
 * top of it to hold the shape.
 *
 * 65% is about as soft as a bare fill goes: it takes the glare off a saturated block while each band
 * still reads as its own colour rather than as a tint of the page. Below roughly 60% the lighter of
 * two series starts to dissolve into the gridlines, and the chart needs an edge back to hold itself
 * together — which is the thing this setting exists to avoid.
 */
const SOFT_FILL_ALPHA = 'a6';

/**
 * The one common chart component every screen should use (`app-chart`, config-driven like the
 * shared form controls) — wraps Chart.js. See `ChartConfig` for the input shape.
 */
@Component({
  selector: 'app-chart',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="ch-host">
      <div class="ch-empty" [class.ch-hidden]="hasData()">
        {{ config.emptyText || 'No data for this period.' }}
      </div>
      <div
        class="ch-canvas-wrap"
        [class.ch-hidden]="!hasData()"
        [style.height.px]="config.height || 220"
      >
        <canvas #canvasRef></canvas>
        @if (isDonutFamily() && hasData()) {
          <div class="ch-donut-center" [class.ch-compact]="config.compact">
            <div class="ch-donut-total">{{ total() }}{{ valueSuffix() }}</div>
            @if (config.centerLabel) {
              <div class="ch-donut-caption">{{ config.centerLabel }}</div>
            }
          </div>
        }
      </div>
    </div>
    <!-- Positioned against the viewport, so no ancestor's overflow can crop it — a canvas tooltip cannot
         leave its own canvas, which on a small plot means it is clipped before it is read. -->
    @if (tipRows().length) {
      <div
        class="ch-tip"
        [class.ch-tip-below]="tipBelow()"
        [style.top.px]="tipPosition().top"
        [style.left.px]="tipPosition().left"
      >
        @for (row of tipRows(); track row.label) {
          <span class="ch-tip-row">
            <i [style.background]="row.color"></i>{{ row.label }}<b>{{ row.value }}</b>
          </span>
          @if (row.note) {
            <span class="ch-tip-note">{{ row.note }}</span>
          }
        }
      </div>
    }
  `,
  styles: [
    `
      .ch-host {
        display: block;
      }
      .ch-empty {
        font-size: 0.82rem;
        color: var(--fg-3, #94a3b8);
        padding: 8px 0;
      }
      .ch-canvas-wrap {
        position: relative;
        width: 100%;
      }
      .ch-hidden {
        display: none;
      }
      .ch-donut-center {
        position: absolute;
        inset: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        pointer-events: none;
      }
      .ch-donut-total {
        font-size: 26px;
        font-weight: 800;
        color: var(--h4c-dark-blue, #262262);
        font-variant-numeric: tabular-nums;
      }
      .ch-donut-caption {
        font-size: 11.5px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--fg-3, #94a3b8);
      }
      .ch-tip {
        position: fixed;
        z-index: 1080;
        pointer-events: none;
        transform: translate(-50%, calc(-100% - 10px));
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding: 7px 10px;
        border-radius: 8px;
        background: rgba(38, 34, 98, 0.95);
        color: #fff;
        font-size: 12px;
        line-height: 1.35;
        white-space: nowrap;
        box-shadow: 0 8px 20px -6px rgba(38, 34, 98, 0.45);
      }
      .ch-tip.ch-tip-below {
        transform: translate(-50%, 10px);
      }
      .ch-tip-row {
        display: flex;
        align-items: center;
        gap: 6px;
      }
      .ch-tip-row i {
        width: 8px;
        height: 8px;
        border-radius: 2px;
        flex-shrink: 0;
      }
      .ch-tip-row b {
        font-weight: 700;
        font-variant-numeric: tabular-nums;
      }
      .ch-tip-note {
        opacity: 0.75;
        font-size: 11px;
      }
      /* A donut small enough to sit in a grid cell: the panel sizes would overflow its hole. */
      .ch-donut-center.ch-compact .ch-donut-total {
        font-size: 15px;
        font-weight: 700;
        line-height: 1.1;
      }
      .ch-donut-center.ch-compact .ch-donut-caption {
        font-size: 9px;
        letter-spacing: 0.03em;
      }
    `,
  ],
})
export class ChartComponent implements AfterViewInit, OnDestroy {
  @ViewChild('canvasRef', { static: true })
  private readonly canvasRef!: ElementRef<HTMLCanvasElement>;

  readonly palette = PALETTE;

  private readonly configData = signal<ChartConfig>({ type: 'bar', points: [] });
  private readonly viewReady = signal(false);
  private chart?: Chart;

  @Input({ required: true }) set config(value: ChartConfig) {
    this.configData.set(value);
  }
  get config(): ChartConfig {
    return this.configData();
  }

  private readonly points = computed<ChartPoint[]>(() => this.configData().points ?? []);

  /** Rows of the DOM tooltip; empty means it isn't showing. Only used when `config.htmlTooltip` is set. */
  readonly tipRows = signal<TooltipRow[]>([]);
  readonly tipPosition = signal<{ top: number; left: number }>({ top: 0, left: 0 });
  /** Set when the anchor is too near the top of the viewport for the tooltip to sit above it. */
  readonly tipBelow = signal(false);

  /** The named series of a multi-series chart, or empty for the single-series case. */
  private readonly series = computed<ChartSeries[]>(() => this.configData().series ?? []);

  readonly hasData = computed(() =>
    this.series().length > 0
      ? (this.configData().labels ?? []).length > 0
      : this.points().length > 0
  );
  readonly total = computed(() => this.points().reduce((sum, p) => sum + p.value, 0));
  readonly valueSuffix = computed(() => this.configData().valueSuffix || '');
  readonly isDonutFamily = computed(() => DONUT_TYPES.has(this.configData().type));

  constructor() {
    effect(() => {
      const cfg = this.configData();
      if (this.viewReady()) this.renderChart(cfg);
    });
  }

  ngAfterViewInit(): void {
    this.viewReady.set(true);
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
  }

  /**
   * Chart.js' `external` tooltip hook: it hands over what would have been drawn, and we render it as the
   * DOM element above instead. Positioned from the canvas' own viewport rect plus the caret, then clamped
   * so it can neither run off the side of the screen nor sit above its top edge.
   */
  private drawHtmlTooltip(
    context: { chart: Chart; tooltip: any },
    points: ChartPoint[],
    colors: string[],
    fmt: (value: number) => string
  ): void {
    const model = context.tooltip;
    if (!model || model.opacity === 0) {
      if (this.tipRows().length) this.tipRows.set([]);
      return;
    }

    const rows: TooltipRow[] = (model.dataPoints ?? []).map((dp: any) => {
      const point = points[dp.dataIndex];
      const names = point?.names ?? [];
      const shown = names.slice(0, MAX_TOOLTIP_NAMES).join(', ');
      return {
        label: point?.label ?? dp.label ?? '',
        value: fmt(point?.value ?? dp.parsed),
        color: colors[dp.dataIndex] ?? colors[0],
        note:
          names.length > MAX_TOOLTIP_NAMES
            ? `${shown} +${names.length - MAX_TOOLTIP_NAMES} more`
            : shown,
      };
    });

    const rect = context.chart.canvas.getBoundingClientRect();
    const anchorTop = rect.top + model.caretY;
    this.tipBelow.set(anchorTop < TOOLTIP_FLIP_MARGIN_PX);
    this.tipPosition.set({
      top: anchorTop,
      left: clamp(
        rect.left + model.caretX,
        TOOLTIP_EDGE_MARGIN_PX,
        window.innerWidth - TOOLTIP_EDGE_MARGIN_PX
      ),
    });
    this.tipRows.set(rows);
  }

  private renderChart(cfg: ChartConfig): void {
    this.chart?.destroy();
    this.chart = undefined;
    this.tipRows.set([]);

    if (!this.canvasRef?.nativeElement) return;

    if (cfg.series?.length) {
      this.renderSeriesChart(cfg);
      return;
    }

    const points = cfg.points ?? [];
    if (!points.length) return;

    const fmt = (value: number) => this.formatValue(cfg, value);
    const colors = points.map(
      (p, i) => p.color ?? cfg.color ?? this.palette[i % this.palette.length]
    );
    const showLegend = cfg.legend ?? this.isDonutFamily();

    const { type, dataset, options } = this.buildChartSpec(
      cfg.type,
      points,
      colors,
      fmt,
      showLegend
    );

    this.chart = new Chart(this.canvasRef.nativeElement, {
      type,
      data: { labels: points.map((p) => p.label), datasets: [dataset] },
      options,
    } as ChartConfiguration);
  }

  /** How a value is printed on an axis tick or in a tooltip — `valueFormat` first, else the plain suffix. */
  private formatValue(cfg: ChartConfig, value: number): string {
    return cfg.valueFormat ? cfg.valueFormat(value) : `${value}${cfg.valueSuffix ?? ''}`;
  }

  /**
   * The multi-series path: one dataset per series over shared labels, stacked by default. Deliberately
   * separate from `buildChartSpec` — that one is built around one dataset whose colours vary per CATEGORY,
   * and the two legends mean opposite things (a category there, a whole series here), so folding them
   * together would cost more than it saves. Donut/pie stay single-series: a stacked pie is not a chart.
   */
  private renderSeriesChart(cfg: ChartConfig): void {
    const labels = cfg.labels ?? [];
    const series = cfg.series ?? [];
    if (!labels.length || !series.length) return;

    const isLine = cfg.type === 'line' || cfg.type === 'area';
    const indexAxis = cfg.type === 'bar' ? 'y' : 'x';
    const stacked = cfg.stacked ?? series.length > 1;
    const colorOf = (s: ChartSeries, i: number) => s.color ?? this.palette[i % this.palette.length];

    const datasets = series.map((s, i) => {
      const color = colorOf(s, i);
      const data = labels.map((_, li) => s.values[li] ?? 0);
      return isLine
        ? {
            label: s.name,
            data,
            borderColor: color,
            backgroundColor: cfg.type === 'area' ? `${color}33` : color,
            pointBackgroundColor: color,
            fill: cfg.type === 'area',
            tension: 0.35,
          }
        : {
            label: s.name,
            data,
            // Lighter than the solid, but with no edge drawn — see `softFill`. Bands stack directly
            // against each other, so the fill has to carry the whole mark on its own.
            backgroundColor: s.softFill ? `${color}${SOFT_FILL_ALPHA}` : color,
            borderWidth: 0,
            // Rounded only on the segment that ends the stack, so the join between two bands stays flat.
            borderRadius: stacked && i < series.length - 1 ? 0 : 4,
            // Chart.js rounds every corner NOT adjacent to the skipped edge, so the default 'start'
            // is what keeps the radius on the free end and leaves the baseline — and, on the top
            // band of a stack, the join it sits on — square. `false` skips nothing and curves both
            // ends, which put a notch in the middle of every stacked column.
            borderSkipped: 'start',
            barPercentage: 0.7,
            categoryPercentage: 0.7,
            maxBarThickness: 40,
          };
    });

    const axisTicks = {
      color: AXIS_LABEL_COLOR,
      font: { family: CHART_FONT_FAMILY, size: 12.5, weight: 500 },
    };
    const valueAxis = indexAxis === 'y' ? 'x' : 'y';

    const options: any = {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: isLine ? 'x' : indexAxis,
      interaction: { mode: 'index', intersect: false },
      scales: {
        [valueAxis]: {
          stacked,
          grid: { color: '#eef1f5' },
          ticks: { ...axisTicks, callback: (v: any) => this.formatValue(cfg, Number(v)) },
        },
        [indexAxis === 'y' ? 'y' : 'x']: { stacked, grid: { display: false }, ticks: axisTicks },
      },
      plugins: {
        // A legend is not optional once there is more than one series — it is the only thing naming them.
        legend: {
          display: cfg.legend ?? true,
          position: 'bottom',
          labels: {
            boxWidth: 12,
            boxHeight: 12,
            color: LEGEND_LABEL_COLOR,
            font: { family: CHART_FONT_FAMILY, size: 13, weight: 600 },
            padding: 14,
            usePointStyle: false,
          },
        },
        tooltip: {
          titleFont: { family: CHART_FONT_FAMILY, size: 13 },
          bodyFont: { family: CHART_FONT_FAMILY, size: 12.5 },
          callbacks: {
            title: (items: any[]) => labels[items[0]?.dataIndex] ?? '',
            label: (ctx: any) =>
              `${ctx.dataset.label}: ${this.formatValue(cfg, Number(ctx.parsed[valueAxis]))}`,
            // A stack's whole height is the figure the reader is usually after, so it is spelled out
            // rather than left to be added up off three tooltip lines.
            footer: stacked
              ? (items: any[]) => {
                  const i = items[0]?.dataIndex ?? 0;
                  const sum = series.reduce((acc, s) => acc + (s.values[i] ?? 0), 0);
                  return `Total: ${this.formatValue(cfg, sum)}`;
                }
              : undefined,
          },
          footerFont: { family: CHART_FONT_FAMILY, size: 12.5, weight: 700 },
        },
      },
    };

    this.chart = new Chart(this.canvasRef.nativeElement, {
      type: isLine ? 'line' : 'bar',
      data: { labels, datasets },
      options,
    } as ChartConfiguration);
  }

  // Chart.js's per-type discriminated options don't unify cleanly across bar/line/doughnut/pie in one
  // return type — this stays loosely typed internally while ChartConfig/ChartPoint keep the public API strict.
  private buildChartSpec(
    type: ChartType,
    points: ChartPoint[],
    colors: string[],
    fmt: (value: number) => string,
    showLegend: boolean
  ): { type: any; dataset: any; options: any } {
    const tooltipLabel = (dataIndex: number, value: number, label: string) => {
      const lines = [`${label}: ${fmt(value)}`];
      const names = points[dataIndex]?.names;
      if (names?.length) {
        const shown = names.slice(0, MAX_TOOLTIP_NAMES).join(', ');
        lines.push(
          names.length > MAX_TOOLTIP_NAMES
            ? `${shown} +${names.length - MAX_TOOLTIP_NAMES} more`
            : shown
        );
      }
      return lines;
    };

    // Bar/line/area charts default to one legend entry per *dataset* (we only ever have one), which is
    // useless here — override generateLabels/onClick so each category gets its own swatch + click-to-toggle,
    // matching how donut/pie legends already work natively per-slice.
    const perPointLegend = {
      generateLabels: (chart: any) =>
        (chart.data.labels as string[]).map((label: string, i: number) => ({
          text: label,
          fillStyle: colors[i],
          strokeStyle: colors[i],
          hidden: !chart.getDataVisibility(i),
          index: i,
        })),
      onClick: (_e: unknown, legendItem: any, legend: any) => {
        legend.chart.toggleDataVisibility(legendItem.index);
        legend.chart.update();
      },
    };

    const legendLabelStyle = {
      boxWidth: 12,
      boxHeight: 12,
      color: LEGEND_LABEL_COLOR,
      font: { family: CHART_FONT_FAMILY, size: 13, weight: 600 },
      padding: 14,
    };

    // Swapping the canvas tooltip for a DOM one is the whole opt-in: the callbacks below stay as they are,
    // and `external` takes over the drawing.
    const htmlTooltip: any = this.configData().htmlTooltip
      ? { enabled: false, external: (ctx: any) => this.drawHtmlTooltip(ctx, points, colors, fmt) }
      : {};

    const baseOptions: any = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: showLegend,
          position: 'bottom',
          labels:
            type === 'donut' || type === 'pie'
              ? legendLabelStyle
              : { ...legendLabelStyle, ...perPointLegend },
        },
        tooltip: {
          titleFont: { family: CHART_FONT_FAMILY, size: 13 },
          bodyFont: { family: CHART_FONT_FAMILY, size: 12.5 },
          callbacks: { title: () => '' },
          ...htmlTooltip,
        },
      },
    };

    if (type === 'donut' || type === 'pie') {
      return {
        type: type === 'donut' ? 'doughnut' : 'pie',
        // A 2px surface-coloured gap between slices: without it two adjacent segments of a related hue
        // (an amber beside an orange) read as one arc. Canvas can't resolve var(), hence the constant.
        dataset: {
          data: points.map((p) => p.value),
          backgroundColor: colors,
          borderColor: CHART_SURFACE,
          borderWidth: 2,
        },
        options: {
          ...baseOptions,
          cutout: type === 'donut' ? '65%' : 0,
          plugins: {
            ...baseOptions.plugins,
            tooltip: {
              ...baseOptions.plugins.tooltip,
              callbacks: {
                title: () => '',
                label: (ctx: any) =>
                  tooltipLabel(
                    ctx.dataIndex,
                    points[ctx.dataIndex].value,
                    points[ctx.dataIndex].label
                  ),
              },
            },
          },
        },
      };
    }

    const axisTicks = {
      color: AXIS_LABEL_COLOR,
      font: { family: CHART_FONT_FAMILY, size: 12.5, weight: 500 },
    };

    if (type === 'line' || type === 'area') {
      const color = colors[0];
      return {
        type: 'line',
        dataset: {
          data: points.map((p) => p.value),
          borderColor: color,
          backgroundColor: type === 'area' ? `${color}33` : color,
          pointBackgroundColor: colors,
          fill: type === 'area',
          tension: 0.35,
        },
        options: {
          ...baseOptions,
          scales: {
            x: { grid: { display: false }, ticks: axisTicks },
            y: {
              grid: { color: '#eef1f5' },
              ticks: { ...axisTicks, callback: (v: any) => fmt(Number(v)) },
            },
          },
          plugins: {
            ...baseOptions.plugins,
            tooltip: {
              callbacks: {
                title: () => '',
                label: (ctx: any) =>
                  tooltipLabel(
                    ctx.dataIndex,
                    points[ctx.dataIndex].value,
                    points[ctx.dataIndex].label
                  ),
              },
            },
          },
        },
      };
    }

    // bar (horizontal) / column (vertical)
    const indexAxis = type === 'bar' ? 'y' : 'x';
    return {
      type: 'bar',
      dataset: {
        data: points.map((p) => p.value),
        backgroundColor: colors,
        borderRadius: 4,
        borderSkipped: false,
        barPercentage: 0.6,
        categoryPercentage: 0.6,
        maxBarThickness: 40,
      },
      options: {
        ...baseOptions,
        indexAxis,
        scales: {
          [indexAxis === 'y' ? 'x' : 'y']: {
            grid: { color: '#eef1f5' },
            ticks: { ...axisTicks, callback: (v: any) => fmt(Number(v)) },
          },
          [indexAxis === 'y' ? 'y' : 'x']: { grid: { display: false }, ticks: axisTicks },
        },
        plugins: {
          ...baseOptions.plugins,
          tooltip: {
            callbacks: {
              title: () => '',
              label: (ctx: any) =>
                tooltipLabel(
                  ctx.dataIndex,
                  points[ctx.dataIndex].value,
                  points[ctx.dataIndex].label
                ),
            },
          },
        },
      },
    };
  }
}
